import { useEffect, useRef, useState } from 'react';

function serialise(value: unknown): string {
  return JSON.stringify(value);
}

export interface SyncedDraft<DraftValue> {
  draft: DraftValue;
  setDraft: (nextDraft: DraftValue) => void;
  baseRevision: number;
  adoptSaved: (savedValue: DraftValue, savedRevision: number) => void;
}

export function useSyncedDraft<DraftValue>(
  savedValue: DraftValue,
  savedRevision: number
): SyncedDraft<DraftValue> {
  const [draft, setDraftState] = useState(savedValue);
  const [baseRevision, setBaseRevision] = useState(savedRevision);
  const lastSavedRef = useRef(serialise(savedValue));
  const lastRevisionRef = useRef(savedRevision);
  const draftRef = useRef(serialise(savedValue));

  function adoptSaved(nextSavedValue: DraftValue, nextSavedRevision: number) {
    const nextSaved = serialise(nextSavedValue);
    lastSavedRef.current = nextSaved;
    lastRevisionRef.current = nextSavedRevision;
    draftRef.current = nextSaved;
    setDraftState(nextSavedValue);
    setBaseRevision(nextSavedRevision);
  }

  useEffect(
    function adoptNewerSavedValueWhilePristine() {
      const nextSaved = serialise(savedValue);
      if (nextSaved === lastSavedRef.current && savedRevision === lastRevisionRef.current) {
        return;
      }
      const wasPristine = draftRef.current === lastSavedRef.current;
      lastSavedRef.current = nextSaved;
      lastRevisionRef.current = savedRevision;
      if (wasPristine) {
        draftRef.current = nextSaved;
        setDraftState(savedValue);
        setBaseRevision(savedRevision);
      }
    },
    [savedValue, savedRevision]
  );

  function setDraft(nextDraft: DraftValue) {
    draftRef.current = serialise(nextDraft);
    setDraftState(nextDraft);
  }

  return { draft, setDraft, baseRevision, adoptSaved };
}
